import { useState, useEffect, useRef } from 'react';
import { crosswalkApi, getApiAuthToken, AGENTIC_API_BASE_URL } from '../services/api';


const LOADING_FALLBACK_MS = 6000;

// The job status-batch endpoint caps the number of job_ids accepted per request.
const JOB_STATUS_BATCH_SIZE = 100;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const INITIAL_RECONNECT_MS = 2000;
const MAX_RECONNECT_MS = 30000;

const ACTIVE_JOB_STATUSES = new Set(['queued', 'running', 'processing', 'polling', 'enriching', 'saving']);

const jobProposalCount = (job) =>
  job.result?.proposals?.count
  ?? job.result?.proposals?.summary?.total_proposals
  ?? 0;

const effectiveJobStatus = (job, proposalCount) => {
  const status = (job.status || '').toLowerCase();
  if (status === 'completed') return proposalCount > 0 ? 'completed' : 'no_proposals';
  if (status === 'failed') return 'failed';
  if (ACTIVE_JOB_STATUSES.has(status)) return 'running';
  return 'not_started';
};


export function useCrosswalkStatusMap(assessmentIds = []) {
  const [statusMap, setStatusMap] = useState({});
  const [eventStatusMap, setEventStatusMap] = useState({});
  const [allJobIds, setAllJobIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const idsRef = useRef(new Set());
  const idsKey = assessmentIds.slice().sort().join(',');



  const jobIdsKey = Array.from(allJobIds).sort().join(',');


  //setup jobids & idsRef
  useEffect(() => {
    if (!idsKey) return;
    const ids = idsKey.split(',').map(Number).filter(Boolean);
    idsRef.current = new Set(ids);

    let cancelled = false;
    crosswalkApi.getAssessmentStatusBatch(ids)
      .then(statuses => {
        if (cancelled) return;
        const jobIds = (statuses || []).flatMap(s => s.jobIds || []).map(String);
        console.log('[Crosswalk] Job IDs from initial batch:', jobIds);
        setAllJobIds(prev => {
          const next = new Set(prev);
          jobIds.forEach(id => next.add(id));
          return next;
        });
      })
      .catch(err => console.error('[Crosswalk] Failed to fetch initial job IDs:', err));

    return () => {
      cancelled = true;
     
    };


  }, [idsKey]);



  //initial fetch counts
  useEffect(() => {
    if (!jobIdsKey) return;
    const jobIds = jobIdsKey.split(',');

    let cancelled = false;
    const url = 'https://myriskshield-agenticapi-staging.azurewebsites.net/crosswalk/assessments/jobs/status-batch';

    const fetchChunk = async (chunk) => {
      try {
        const token = getApiAuthToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          console.warn('[Crosswalk] No auth token available');
        }

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ job_ids: chunk }),
        });

        if (!response.ok) {
          console.error('[Crosswalk] Job status-batch chunk failed:', response.status, response.statusText);
          return null;
        }

        return await response.json();
      } catch (error) {
        console.error('[Crosswalk] Job status-batch chunk error:', error);
        return null;
      }
    };

    const fetchJobStatusBatch = async () => {
      const chunks = chunkArray(jobIds, JOB_STATUS_BATCH_SIZE);
      const results = await Promise.all(chunks.map(fetchChunk));

      if (cancelled) return;
      const merged = results.reduce(
        (acc, result) => {
          if (!result) return acc;
          acc.jobs.push(...(result.jobs || []));
          acc.missing_job_ids.push(...(result.missing_job_ids || []));
          acc.completed_count += result.completed_count || 0;
          acc.failed_count += result.failed_count || 0;
          acc.queued_count += result.queued_count || 0;
          acc.running_count += result.running_count || 0;
          return acc;
        },
        { jobs: [], missing_job_ids: [], completed_count: 0, failed_count: 0, queued_count: 0, running_count: 0 }
      );
      // console.log('[Crosswalk] Job status-batch result (merged):', merged);

      const byAssessment = new Map();
      merged.jobs.forEach((job) => {
        const assessmentId = job.input?.assessment_id;
        if (!byAssessment.has(assessmentId)) {
          byAssessment.set(assessmentId, { assessment_id: assessmentId, jobs: [] });
        }
        byAssessment.get(assessmentId).jobs.push({
          questionnaire_id: job.input?.questionnaire_id,
          job_id: job.job_id,
          status: job.status,
          phase: job.phase,
          updated_at: job.updated_at,
        });
      });
      const groupedByAssessment = Array.from(byAssessment.values());
      // console.log('[Crosswalk] Job status-batch result (grouped by assessment):', groupedByAssessment);

      setStatusMap(prev => {
        const next = { ...prev };
        groupedByAssessment.forEach(({ assessment_id, jobs }) => {
          const completedCount = jobs.filter(j => (j.status || '').toLowerCase() === 'completed').length;
          const failedCount = jobs.filter(j => (j.status || '').toLowerCase() === 'failed').length;
          const runningCount = jobs.filter(j => ACTIVE_JOB_STATUSES.has((j.status || '').toLowerCase())).length;
          const status = failedCount > 0
            ? 'failed'
            : runningCount > 0
              ? 'running'
              : completedCount === jobs.length
                ? 'completed'
                : 'not_started';
          const lastUpdatedAt = jobs.reduce(
            (latest, j) => (j.updated_at && (!latest || j.updated_at > latest) ? j.updated_at : latest),
            null
          );

          next[assessment_id] = {
            status,
            questionnaireCount: jobs.length,
            completedCount,
            runningCount,
            failedCount,
            lastUpdatedAt,
            loading: false,
          };
        });
        return next;
      });

      setLoading(false);
    };

    fetchJobStatusBatch();

    return () => {
      cancelled = true;
    };
  }, [jobIdsKey])


  // console.log("allJobIds",allJobIds)

  //live update via SSE

  useEffect(() => {
    const currentIds = Array.from(idsRef.current).join(',');

    const CountFromEvents=[]


    if (!currentIds) {
      console.log('[Event API] No assessment IDs available, skipping SSE connection');
      return; 
    }

    let isCancelled = false;
    let abortController = null;
    let reconnectTimer = null;
    let retryDelay = INITIAL_RECONNECT_MS;

    const scheduleReconnect = () => {
      if (isCancelled) return;
      console.log(`[Event API] Reconnecting SSE in ${retryDelay}ms`);
      reconnectTimer = setTimeout(connectSSE, retryDelay);
      // Exponential backoff (capped) so a persistently-down endpoint isn't hammered.
      retryDelay = Math.min(retryDelay * 2, MAX_RECONNECT_MS);
    };

    const connectSSE = async () => {
      if (isCancelled) return;
      abortController = new AbortController();
      try {
        const sseUrl = `${AGENTIC_API_BASE_URL}/crosswalk/assessments/events?assessment_ids=${currentIds}`;
        const token = getApiAuthToken();
        const headers = {
          'Accept': 'text/event-stream',
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          console.warn('[Event API] No auth token available');
        }

        const response = await fetch(sseUrl, {
          headers,
          signal: abortController.signal,
        });

        if (!response.ok) {
          console.error('[Event API] SSE connection failed:', response.status, response.statusText);
          scheduleReconnect();
          return;
        }

        // Connected successfully — reset the backoff so the next drop retries quickly.
        retryDelay = INITIAL_RECONNECT_MS;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!isCancelled) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; 

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6); 

              try {
                const parsed = JSON.parse(data);
                console.log('[Event API] Parsed data:', parsed, Date.now());
                console.log("dev fetch live data based on assessment id ", parsed.assessment_id, parsed);
                if (parsed.aggregate_status === 'idle') {
                  continue;
                }
                if (parsed.assessment_id && idsRef.current.has(parsed.assessment_id)) {


              
      
                  setEventStatusMap(prev => ({
                    ...prev,
                    [parsed.assessment_id]: {
                      latestStatus: parsed.aggregate_status,
                      latestMessage: parsed.latest_message,
                    },
                  }));

                  setStatusMap(prev => ({
                    ...prev,
                    [parsed.assessment_id]: {
                      status: parsed.aggregate_status,
                      questionnaireCount:parsed.total_jobs,
                      completedCount:parsed.completed_jobs,
                      runningCount:parsed.active_jobs,
                      failedCount:parsed.failed_jobs,
                      lastUpdatedAt: parsed.updated_at,
                      loading: false,
                    }
                  }))
                }

              } catch (error) {
                console.error('[Event API] Error parsing message:', error);
              }
            }
          }
        }

        // Stream closed by the server (e.g. idle timeout) — reconnect unless we're tearing down.
        if (!isCancelled) {
          console.log('[Event API] SSE stream ended, will reconnect');
          scheduleReconnect();
        }

      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('[Event API] SSE connection aborted');
        } else {
          console.error('[Event API] SSE connection error:', error);
          scheduleReconnect();
        }
      }
    };

    connectSSE();

    return () => {
      console.log('[Event API] Closing SSE connection');
      isCancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (abortController) abortController.abort();
    };
  }, [idsKey]); 

  return { statusMap, eventStatusMap, allJobIds,eventsLoading: loading };
}
